// Global Instructions Rule Applied!
//
// Agent drafts — in-app suggestions an internal agent wrote for the
// responsible team member (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md
// Phase 0). Shape = agent_drafts__row (view__agent_drafts /
// view__my_agent_drafts). Nothing here is ever sent by Reservoir: Copy puts
// the text on the clipboard, Send to Outlook (Phase 6a) creates the draft in
// the user's OWN Outlook Drafts folder for them to edit and send there.
// Lifecycle: proposed → exported ("In Outlook") → sent (Phase 6b links the
// outbound interaction: exact by internetMessageId, fuzzy candidate the user
// confirms, or Mark as sent) | accepted (copied) | dismissed | superseded |
// expired.
import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'

export const AgentDraftContactSchema = z
  .object({
    contacts__id: z.string(),
    contacts__name: z.string().nullish(),
    contacts__email: z.string().nullish()
  })
  .strict()

export const AgentDraftEvidenceSchema = z
  .object({
    kind: z.string().nullish(),
    id: z.string().nullish(),
    excerpt: z.string().nullish(),
    label: z.string().nullish(),
    occurred_at: z.string().nullish()
  })
  .passthrough()

/** An email the draft is (or may be) linked to — the sent interaction, the
 *  fuzzy candidate, or an option in the Mark as sent picker. */
export const AgentDraftInteractionRefSchema = z
  .object({
    interactions__id: z.string(),
    interactions__subject: z.string().nullish(),
    interactions__datetime: z.string().nullish(),
    interactions__to_names: z.string().nullish()
  })
  .strict()
export type AgentDraftInteractionRef = z.infer<typeof AgentDraftInteractionRefSchema>

export const AgentDraftExportedSiblingSchema = z
  .object({
    agent_drafts__id: z.string(),
    agent_drafts__exported_at: z.string().nullish(),
    agent_drafts__outlook_web_link: z.string().nullish()
  })
  .strict()

/** Is Send to Outlook available to the caller — rides on the list envelopes. */
export const OutlookDraftCapabilitySchema = z
  .object({
    enabled: z.boolean(),
    global_enabled: z.boolean().nullish(),
    mailbox_enabled: z.boolean().nullish(),
    mailbox: z.string().nullish()
  })
  .passthrough()
export type OutlookDraftCapability = z.infer<typeof OutlookDraftCapabilitySchema>
export const OUTLOOK_CAPABILITY_OFF: OutlookDraftCapability = { enabled: false, global_enabled: false, mailbox_enabled: false, mailbox: null }

export const AgentDraftRecordSchema = z
  .object({
    agent_drafts__id: z.string(),
    agent_drafts__context_table: z.string(),
    agent_drafts__context_id: z.string(),
    agent_drafts__account_id: z.string(),
    agent_drafts__subject: z.string().nullish(),
    agent_drafts__body_markdown: z.string(),
    agent_drafts__rationale: z.string().nullish(),
    agent_drafts__dedupe_key: z.string(),
    agent_drafts__status: z.string(),
    agent_drafts__status_reason: z.string().nullish(),
    agent_drafts__accepted_action: z.string().nullish(),
    agent_drafts__reviewed_at: z.string().nullish(),
    agent_drafts__superseded_by_id: z.string().nullish(),
    agent_drafts__outlook_mailbox: z.string().nullish(),
    agent_drafts__outlook_message_id: z.string().nullish(),
    agent_drafts__outlook_web_link: z.string().nullish(),
    agent_drafts__outlook_is_reply: z.boolean().nullish(),
    agent_drafts__exported_at: z.string().nullish(),
    agent_drafts__outlook_item_missing_at: z.string().nullish(),
    agent_drafts__outlook_last_checked_at: z.string().nullish(),
    agent_drafts__outlook_sent_at: z.string().nullish(),
    agent_drafts__sent_interaction_id: z.string().nullish(),
    agent_drafts__sent_at: z.string().nullish(),
    agent_drafts__sent_match_kind: z.string().nullish(),
    agent_drafts__candidate_interaction_id: z.string().nullish(),
    agent_drafts__candidate_score: z.number().nullish(),
    agent_drafts__candidate_found_at: z.string().nullish(),
    agent_drafts__date_created: z.string(),
    agent_drafts__date_modified: z.string().nullish(),
    agent_runs__id: z.string().nullish(),
    agent_draft_kinds__kind_key: z.string(),
    agent_draft_kinds__label: z.string(),
    internal_agent_types__agent_type_key: z.string(),
    internal_agent_types__name: z.string(),
    accounts__name: z.string(),
    prospects__id: z.string().nullish(),
    prospects__name: z.string().nullish(),
    funds__name: z.string().nullish(),
    system_users__id: z.string().nullish(),
    prog__assigned_to_name: z.string().nullish(),
    prog__reviewed_by_name: z.string().nullish(),
    prog__exported_by_name: z.string().nullish(),
    prog__exported_sibling: AgentDraftExportedSiblingSchema.nullish(),
    prog__sent_interaction: AgentDraftInteractionRefSchema.nullish(),
    prog__candidate_interaction: AgentDraftInteractionRefSchema.nullish(),
    prog__suggested_to: z.array(AgentDraftContactSchema),
    prog__suggested_cc: z.array(AgentDraftContactSchema),
    prog__evidence: z.array(AgentDraftEvidenceSchema)
  })
  .strict()
export type AgentDraftRecord = z.infer<typeof AgentDraftRecordSchema>
export const AgentDraftRecordFields = makeFieldKeys(AgentDraftRecordSchema)
export const validateAgentDraftsList = (data: unknown): AgentDraftRecord[] =>
  validateWithZodSchema(z.array(AgentDraftRecordSchema), data, 'agent drafts') as AgentDraftRecord[]
export const validateAgentDraft = (data: unknown): AgentDraftRecord =>
  validateWithZodSchema(AgentDraftRecordSchema, data, 'agent draft') as AgentDraftRecord
export const validateOutlookDraftCapability = (data: unknown): OutlookDraftCapability =>
  data == null ? OUTLOOK_CAPABILITY_OFF : (validateWithZodSchema(OutlookDraftCapabilitySchema, data, 'outlook draft capability') as OutlookDraftCapability)
